/**
 * EvilAdminPanel — admin-only "fun" console, faithful to legacy
 * DefineSprite_285_evilAdminWindow. Lays out the same icon grid + forms:
 *
 *   ┌── Evil Admin Panel ───────────────────────────────[×]┐
 *   │ [💣 BOMB] [↻ RESET] [🌐 GLOBE] [✋ SLAP] [✋✋ SLAP ALL]│
 *   │ [🦭 WALRUS] [🪣 BUCKET]                                │
 *   │                                                        │
 *   │  change all statuses to: [____]    [SET]              │
 *   │  selected user: [____]   msg: [_______]   [SEND]      │
 *   │  random user:           msg: [_______]   [SEND]       │
 *   └────────────────────────────────────────────────────────┘
 *
 * Every action maps to a server cmd we already implement:
 *   bomb / walrus / bucket / slap_all_users / user_status_all
 *   user_status (selected user) / user_mess (selected) / user_mess (random)
 */
import { useState } from 'react';
import { Modal } from './Modal.js';
import type { UserListEntry } from '../lib/types.js';

interface Props {
    room: string;
    users: UserListEntry[];
    onClose: () => void;
    onSend: (frame: object) => void;
}

export function EvilAdminPanel({ room, users, onClose, onSend }: Props) {
    const [statusAll, setStatusAll] = useState(0);
    const [selUser,   setSelUser]   = useState('');
    const [selMsg,    setSelMsg]    = useState('');
    const [randMsg,   setRandMsg]   = useState('');

    const send = (frame: object) => onSend(frame);
    const randomUser = (): string | null => {
        if (users.length === 0) return null;
        return users[Math.floor(Math.random() * users.length)]!.name;
    };

    return (
        <Modal title="Evil Admin Panel" onClose={onClose} className="pc-modal-evil">
            <div className="eap-icon-row">
                <button className="eap-icon eap-icon-bomb"   onClick={() => send({ cmd: 'bomb',   room })} title="Drop a bomb">💣</button>
                <button className="eap-icon eap-icon-reset"  onClick={() => send({ cmd: 'user_status_all', room, s: 0 })} title="Reset all statuses to 0">↻</button>
                <button className="eap-icon eap-icon-globe"  onClick={() => send({ cmd: 'user_status_all', room, s: Math.floor(Math.random()*5) })} title="Random status for all">🌐</button>
                <button className="eap-icon eap-icon-slap"
                        onClick={() => {
                            const u = selUser.trim() || randomUser();
                            if (u) send({ cmd: 'slap_user', room, user: u });
                        }} title="Slap selected (or random)">✋</button>
                <button className="eap-icon eap-icon-slap-all" onClick={() => send({ cmd: 'slap_all_users', room })} title="Slap everyone">✋✋</button>
            </div>
            <div className="eap-icon-row">
                <button className="eap-icon" onClick={() => send({ cmd: 'walrus', room })} title="Summon the walrus" style={{ fontSize: 24 }}>🦭</button>
                <button className="eap-icon" onClick={() => send({ cmd: 'bucket', room })} title="Throw the bucket"  style={{ fontSize: 24 }}>🪣</button>
            </div>

            <div className="eap-row">
                <label>change all statuses to</label>
                <input type="number" min={0} max={9} value={statusAll}
                       onChange={e => setStatusAll(Number(e.target.value || 0))} />
                <button className="pw-btn"
                        onClick={() => send({ cmd: 'user_status_all', room, s: statusAll })}>SET</button>
            </div>

            <div className="eap-row">
                <label>selected user</label>
                <input type="text" maxLength={64}
                       value={selUser}
                       onChange={e => setSelUser(e.target.value)}
                       placeholder="username" />
                <input type="text" maxLength={200}
                       value={selMsg}
                       onChange={e => setSelMsg(e.target.value)}
                       placeholder="message" />
                <button className="pw-btn" disabled={!selUser.trim() || !selMsg.trim()}
                        onClick={() => {
                            const target = users.find(u => u.name === selUser.trim());
                            if (!target) { alert(`No user named "${selUser.trim()}"`); return; }
                            send({ cmd: 'user_mess', room, to_uid: target.id, m: selMsg.trim() });
                        }}>SEND</button>
            </div>

            <div className="eap-row">
                <label>random user</label>
                <span className="eap-spacer">(picks one in this room)</span>
                <input type="text" maxLength={200}
                       value={randMsg}
                       onChange={e => setRandMsg(e.target.value)}
                       placeholder="message" />
                <button className="pw-btn" disabled={!randMsg.trim()}
                        onClick={() => {
                            const u = randomUser();
                            if (!u) { alert('No users to target'); return; }
                            const target = users.find(x => x.name === u);
                            if (!target) return;
                            send({ cmd: 'user_mess', room, to_uid: target.id, m: randMsg.trim() });
                        }}>SEND</button>
            </div>
        </Modal>
    );
}
